import { Loader } from "@findemdev/maui";
import React, { Suspense, useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import PrivateRouteWrapper from "../components/PermissionedComponents/PrivateRouteWrapper";
import LostPath from "../components/PermissionedComponents/ResourceNotFound";
import InstalledApp from "../components/InstalledApp/InstalledApp";
import { StyledContentWrapper } from "../App.styled";
import { getAppName } from "../utils/common";
import { APP_NAMES } from "../types/app";
import IframePage from "../components/Iframe/IframePage";
import { ADMIN_IFRAME_ROUTES } from "../constants/iframeRoutes";
import { hasUserAgentAccess } from "../common/utils/accessUtils";
import UserContext from "../contexts/UserContext";
import SearchInsightsRedirect from "./SearchInsightsRedirect";

const MyApps = React.lazy(() => import("../pages/MyApps"));
const AppsCatalog = React.lazy(() => import("../pages/AppsCatalog"));
const RoadmapPage = React.lazy(() => import("../pages/Roadmap"));
const RoadmapDetailsPage = React.lazy(() => import("../pages/RoadmapDetails"));
const DashboardsPage = React.lazy(() => import("../pages/Dashboards"));
const Credential = React.lazy(
  () => import("../components/CredentialApp/components/Credential/Credential"),
);
const CompanyBuilder = React.lazy(
  () => import("../components/CompanyBuilder/components/Builder/Builder"),
);
const RaasClientProjectPage = React.lazy(
  () => import("../components/RaasClientPortal/RaasClientProjectPage"),
);
const AccountManagement = React.lazy(
  () => import("../components/AccountManagement/AccountManagement"),
);

interface ICustomRoutesProps {
  isSidebarCollapsed: boolean;
  hideLayoutBars: boolean;
}

const appName = getAppName();

const CustomRoutes = (props: ICustomRoutesProps) => {
  const { isSidebarCollapsed, hideLayoutBars } = props;
  const userModel = useContext(UserContext);
  const shouldShowRaasInternalPortal =
    userModel.canAccessRaasInternalPortal();

  return (
    <PrivateRouteWrapper>
      <Routes>
        <Route path="*" key={"no-path-found"} element={<LostPath />} />

        <Route path="/" element={<Navigate to="/my-apps" />} />
        <Route
          path="/search_insights/*"
          element={<SearchInsightsRedirect />}
        />
        <Route path="/my-apps">
          <Route
            index
            key={"my-apps"}
            element={
              <Suspense fallback={<Loader $fullPage={true} />}>
                <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                  <MyApps
                    hideLayoutBars={hideLayoutBars}
                    isSidebarCollapsed={isSidebarCollapsed}
                  />
                </StyledContentWrapper>
              </Suspense>
            }
          />
          <Route
            path=":id"
            key={"my-apps/:id"}
            element={
              <Suspense fallback={<Loader $fullPage={true} />}>
                <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                  <InstalledApp hideLayoutBars={hideLayoutBars} />
                </StyledContentWrapper>
              </Suspense>
            }
          />
          <Route
            path="/my-apps/cred-builder/credentials/:credentialId"
            element={
              <Suspense fallback={<Loader $fullPage={true} />}>
                <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                  <Credential />
                </StyledContentWrapper>
              </Suspense>
            }
          />
          <Route
            path="/my-apps/company-builder/company-list/:companyListId"
            element={
              <Suspense fallback={<Loader $fullPage={true} />}>
                <CompanyBuilder />
              </Suspense>
            }
          />
          <Route
            path="/my-apps/raas-client-portal/projects/:id"
            element={
              <Suspense fallback={<Loader $fullPage={true} />}>
                <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                  <RaasClientProjectPage />
                </StyledContentWrapper>
              </Suspense>
            }
          />
          {shouldShowRaasInternalPortal && (
            <Route
              path="/my-apps/raas-internal-portal/projects/:id"
              element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                    <RaasClientProjectPage />
                  </StyledContentWrapper>
                </Suspense>
              }
            />
          )}
        </Route>

        <Route path="/apps-catalog">
          <Route
            index
            key={"apps-catalog"}
            element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <AppsCatalog
                    hideLayoutBars={hideLayoutBars}
                    isSidebarCollapsed={isSidebarCollapsed}
                  />
                </Suspense>
              }
            />
          <Route
            path=":id"
            key={"apps-catalog/:id"}
            element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <InstalledApp hideLayoutBars={hideLayoutBars} />
                </Suspense>
              }
            />
        </Route>

        {appName === APP_NAMES.RRA && (
          <>
            <Route
              path="/rra-universe"
              key={"rra-universe"}
              element={<>RRA Universe</>}
            />
            <Route
              path="/roadmap"
              key={"roadmap"}
              element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <RoadmapPage />
                </Suspense>
              }
            />
            <Route
              path="/roadmap/:id"
              key={"roadmap"}
              element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <RoadmapDetailsPage />
                </Suspense>
              }
            />
            <Route
              path="/dashboards"
              key={"dashboards"}
              element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <DashboardsPage isSidebarCollapsed={isSidebarCollapsed} />
                </Suspense>
              }
            />
          </>
        )}
        {appName === APP_NAMES.ESP && (
          <>
            <Route
              path="/account-management"
              key="account-management"
              element={
                <Suspense fallback={<Loader $fullPage={true} />}>
                  <AccountManagement />
                </Suspense>
              }
            />
            {ADMIN_IFRAME_ROUTES.map(({ path, iframeRoute }) => (
              <Route
                path={path}
                key={path}
                element={
                  <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                    <IframePage
                      hideLayoutBars={hideLayoutBars}
                      iframeRoute={iframeRoute}
                    />
                  </StyledContentWrapper>
                }
              />
            ))}
          </>
        )}
        {hasUserAgentAccess(userModel?.getUser()?.roles ?? []) && (
          <Route
            path="/chat"
            key="chat"
            element={
              <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                <IframePage
                  hideLayoutBars={hideLayoutBars}
                  iframeRoute={"https://app-next-main.findem.ai/agents"}
                />
              </StyledContentWrapper>
            }
          />
        )}
        {hasUserAgentAccess(userModel?.getUser()?.roles ?? []) && (
          <Route
            path="/exec_projects"
            key="exec_projects"
            element={
              <StyledContentWrapper $sidebarCollapsed={isSidebarCollapsed}>
                <IframePage
                  hideLayoutBars={hideLayoutBars}
                  iframeRoute={appName === APP_NAMES.RRA ? "https://rra-staging.findem.ai/exec_projects" : "https://app-next-main.findem.ai/exec_projects"}
                />
              </StyledContentWrapper>
            }
          />
        )}
      </Routes>
    </PrivateRouteWrapper>
  );
};

export default CustomRoutes;
